/**
 * ResetPassword — public page that completes a forgot-password flow.
 *
 * Reads the token from the URL query string, prompts for a new password
 * (with confirm), and submits to `/api/auth/reset-password`. If the API
 * responds with `{ mfa_required: true }` it re-prompts for a TOTP / backup
 * code and re-submits with `mfaCode` included.
 *
 * Issue: #320
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react';
import { tenantName, tenantLogo } from '../config/tenant';
import { resetPassword } from '../services/authApi';
import { evaluatePasswordStrength } from '../utils/passwordStrength';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const strength = useMemo(() => evaluatePasswordStrength(newPassword), [newPassword]);
  const allRulesPass = strength.every((r) => r.ok);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = !!token && allRulesPass && passwordsMatch && (!mfaRequired || mfaCode.length >= 6);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => navigate('/login'), 3000);
      return () => clearTimeout(t);
    }
  }, [success, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await resetPassword(token, newPassword, mfaRequired ? mfaCode : undefined);
      if (result.mfa_required) {
        setMfaRequired(true);
        setMfaCode('');
        return;
      }
      if (result.success) {
        setSuccess(true);
        return;
      }
      setError('Unexpected response from the server. Please try again.');
    } catch (err: any) {
      const apiMsg = err?.response?.data?.error;
      const details = err?.response?.data?.details;
      if (Array.isArray(details) && details.length > 0) {
        setError(details.join(' • '));
      } else if (typeof apiMsg === 'string') {
        setError(apiMsg);
      } else {
        setError('Could not complete the password reset. The link may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ems-bg">
      <div className="w-full max-w-xl px-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-12">
          <div className="text-center mb-10">
            <img
              src={tenantLogo}
              alt={tenantName}
              className="w-72 h-72 mx-auto mb-6 object-contain"
            />
          </div>

          {!token ? (
            <div className="space-y-4 text-center">
              <h2 className="text-lg font-semibold text-gray-900">Invalid reset link</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                This page must be reached from the link in your password-reset email.
              </p>
              <Link to="/forgot-password" className="inline-flex items-center gap-1 text-sm text-ems-green hover:underline">
                <ArrowLeft className="w-3.5 h-3.5" />
                Request a new link
              </Link>
            </div>
          ) : success ? (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="w-10 h-10 text-ems-green mx-auto" />
              <h2 className="text-lg font-semibold text-gray-900">Password updated</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                You can now sign in with your new password. Redirecting…
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-center mb-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  {mfaRequired ? 'Two-factor verification' : 'Choose a new password'}
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  {mfaRequired
                    ? 'Enter the 6-digit code from your authenticator app to complete the reset.'
                    : 'Pick a strong password you don’t use elsewhere.'}
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-ems-red/10 border border-ems-red/20 text-ems-red text-sm">
                  {error}
                </div>
              )}

              {!mfaRequired && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      New password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
                      placeholder="New password"
                      required
                      autoComplete="new-password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Confirm password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
                      placeholder="Confirm password"
                      required
                      autoComplete="new-password"
                    />
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <p className="mt-1 text-xs text-ems-red">Passwords do not match.</p>
                    )}
                  </div>

                  <ul className="space-y-1 text-xs">
                    {strength.map((rule) => (
                      <li
                        key={rule.label}
                        className={rule.ok ? 'text-ems-green' : 'text-gray-500'}
                      >
                        {rule.ok ? '✓' : '•'} {rule.label}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {mfaRequired && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1.5">
                    Verification code
                  </label>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-ems-green" />
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={8}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                      className="flex-1 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-center text-lg tracking-widest outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
                      placeholder="000000"
                      required
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">You can also enter a backup code.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !canSubmit}
                className="w-full py-3 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 font-semibold text-base transition-colors hover:bg-ems-forest/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : mfaRequired ? 'Verify and update' : 'Update password'}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-sm text-ems-green hover:underline"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </div>

        <div className="mt-6 flex items-center justify-center">
          <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-xl border border-gray-200">
            <img
              src="/djin-logo.png"
              alt="DJIN Energy Management Systems"
              className="w-9 h-9 rounded-full object-contain"
            />
            <div>
              <p className="text-xs text-gray-500 font-medium">Powered by</p>
              <p className="text-sm font-bold text-gray-900">DJIN Energy Management Systems</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
